import {
    keyBy,
    map,
    merge,
    uniq,
    filter,
    isNil,
    isEmpty,
    get,
    cloneDeep,
    size,
    unionBy,
} from 'lodash';
import to from 'await-to-js';
import axios from 'axios';
import i18n from '@/js/vue-i18n';
import attributeFilterValueSelectionModes from '@enums/attribute-filter-value-selection-modes';

import storeMixin from '@/js/store/mixins/vuex-store';
import { isProductMatchSupplierAndCategory } from '@/js/utils/products-utils';

const sanitizeOptionValues = values => (values || []).filter(value => !isNil(value));

const getInitialState = () => ({
    products: [],
    brands: [],
    suppliers: [],
});

const promotionAttributeFilterRequestsByScope = new Map();
const DEFAULT_PROMOTION_ATTRIBUTE_FILTER_SCOPE_TOKEN = '__default__';

const getPromotionAttributeFilterScopeKey = scopeToken =>
    scopeToken || DEFAULT_PROMOTION_ATTRIBUTE_FILTER_SCOPE_TOKEN;

const getPromotionAttributeFilterRequestState = scopeToken => {
    const scopeKey = getPromotionAttributeFilterScopeKey(scopeToken);

    if (!promotionAttributeFilterRequestsByScope.has(scopeKey)) {
        promotionAttributeFilterRequestsByScope.set(scopeKey, {
            abortController: null,
            requestSequence: 0,
        });
    }

    return promotionAttributeFilterRequestsByScope.get(scopeKey);
};

const clearPromotionAttributeFilterAbortControllerIfCurrent = ({
    requestState,
    requestSequence,
}) => {
    if (requestState.requestSequence === requestSequence) {
        requestState.abortController = null;
    }
};

const isRequestCancelled = error =>
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

const normalizeFilterCategories = categories =>
    (categories || [])
        .map(category => {
            if (category && typeof category === 'object') {
                return category.levelEntryKey;
            }

            return category;
        })
        .filter(category => !isNil(category));

const RANGE_OPERATORS = ['gte', 'between', 'lte'];
const VALUE_SELECTION_MODES = Object.values(attributeFilterValueSelectionModes);

const { INCLUDE } = attributeFilterValueSelectionModes;

const isMultipleAttributeSegmentsEnabled = rootState =>
    get(rootState, 'clientConfig.toggleLogic.enableMultipleAttributeSegments', false);

const normalizeNumericBoundary = value => {
    if (isNil(value) || value === '') {
        return undefined;
    }

    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : value;
};

const normalizeOrderedSelections = orderedSelections =>
    (orderedSelections || []).map(selection => {
        const key = isNil(selection?.key) || selection.key === '' ? null : String(selection.key);
        const attributeKey = isNil(selection?.attributeKey) ? null : Number(selection.attributeKey);
        const mode = VALUE_SELECTION_MODES.includes(selection?.mode) ? selection.mode : INCLUDE;
        const rangeOperator = RANGE_OPERATORS.includes(selection?.rangeOperator)
            ? selection.rangeOperator
            : null;

        if (!rangeOperator) {
            const normalizedSelection = {
                attributeKey,
                values: selection?.values || [],
                mode,
            };

            if (key) {
                normalizedSelection.key = key;
            }

            return normalizedSelection;
        }

        if (rangeOperator === 'gte') {
            const normalizedSelection = {
                attributeKey,
                mode,
                rangeOperator,
                minValue: normalizeNumericBoundary(selection?.minValue),
            };

            if (key) {
                normalizedSelection.key = key;
            }

            return normalizedSelection;
        }

        if (rangeOperator === 'lte') {
            const normalizedSelection = {
                attributeKey,
                mode,
                rangeOperator,
                maxValue: normalizeNumericBoundary(selection?.maxValue),
            };

            if (key) {
                normalizedSelection.key = key;
            }

            return normalizedSelection;
        }

        const normalizedSelection = {
            attributeKey,
            mode,
            rangeOperator,
            minValue: normalizeNumericBoundary(selection?.minValue),
            maxValue: normalizeNumericBoundary(selection?.maxValue),
        };

        if (key) {
            normalizedSelection.key = key;
        }

        return normalizedSelection;
    });

const normalizeAttributeSegments = segments =>
    (segments || []).map((segment, segmentIndex) => ({
        id: segment.id || segment.segmentId || segment.key || String(segmentIndex),
        key: segment.key || segment.id || segment.segmentId || null,
        label: isNil(segment.label) ? null : segment.label,
        rows: normalizeOrderedSelections(segment.orderedSelections || segment.rows || []),
    }));

const getAttributeFilterPayload = ({ categories, orderedSelections, segments, scopeToken }) => {
    const payload = {
        categories,
        scopeToken,
    };

    if (segments && segments.length) {
        payload.segments = segments;
        return payload;
    }

    payload.orderedSelections = orderedSelections;
    return payload;
};

const applyAttributeFilterResponseToRows = ({ attributeScope = [], response = {} } = {}) => {
    const availableAttributeKeysByRowKey = {};
    const availableValuesByRowKey = {};
    const responseSelections = get(response, 'orderedSelections', []);
    const responseSelectionsByRowIndex = keyBy(responseSelections, 'rowIndex');
    const responseSelectionsByRowKey = keyBy(
        responseSelections.filter(selection => !isNil(selection.key)),
        'key'
    );

    attributeScope.forEach((row, rowIndex) => {
        const selection =
            responseSelectionsByRowKey[row.key] ||
            responseSelectionsByRowIndex[rowIndex] ||
            responseSelections[rowIndex] ||
            {};
        const availableAttributeKeys = sanitizeOptionValues(selection.availableAttributeKeys);
        const selectedValues = sanitizeOptionValues(selection.selectedValues);
        const availableValues = sanitizeOptionValues(selection.availableValues);
        const rangeOperator = selection.rangeOperator || row.rangeOperator || null;
        const normalizedSelectionMode = VALUE_SELECTION_MODES.includes(selection.mode)
            ? selection.mode
            : null;
        const normalizedRowMode = VALUE_SELECTION_MODES.includes(row.mode) ? row.mode : null;
        const normalizedMode = normalizedSelectionMode || normalizedRowMode || INCLUDE;

        if (rangeOperator) {
            row.rangeOperator = rangeOperator;
            row.minValue = isNil(selection.minValue) ? row.minValue : selection.minValue;
            row.maxValue = isNil(selection.maxValue) ? row.maxValue : selection.maxValue;
            row.values = [];
            row.mode = normalizedMode;
        } else {
            row.rangeOperator = null;
            row.minValue = null;
            row.maxValue = null;
            row.values = selectedValues;
            row.mode = normalizedMode;
        }

        row.isRangeCapable = selection.isRangeCapable === true;
        availableAttributeKeysByRowKey[row.key] = availableAttributeKeys;
        availableValuesByRowKey[row.key] = availableValues;
    });

    return {
        scopedProductCount: get(response, 'scopedProductCount', 0),
        invalidatedSelections: get(response, 'invalidatedSelections', []),
        availableAttributeKeysByRowKey,
        availableValuesByRowKey,
    };
};

/**
 * Inherits from the default store mixin which takes care of all CRUD operations.
 */
const store = {
    namespaced: true,

    /**
     * Default state available:
     * - loading
     * - filter
     */

    state: {
        ...getInitialState(),
        productsWithMinimalData: [],
        productsForPromotionProductOfferGroups: {},
    },

    /**
     * Default getters available:
     * - getProductById
     * - getFilteredProducts
     */
    getters: {
        getProductsCategories: () => products => {
            return uniq(map(products, 'category'));
        },

        productByKeyMap: state => keyBy(state.products, 'productKey'),
        getProductByKey: (state, getter) => productKey => getter.productByKeyMap[productKey],
        getProductsBySupplierAndCategory: (state, getters, rootState, rootGetters) => ({
            supplierKey,
            categoryKeys,
        }) => {
            const { categoryLevel } = rootGetters['clientConfig/getHierarchyConfig'];
            const filteredProducts = filter(state.products, product =>
                isProductMatchSupplierAndCategory({
                    product,
                    supplierKey,
                    categoryKeys,
                    categoryLevel,
                })
            );
            return map(filteredProducts, product => ({
                ...product,
                displayLabel: product.isProposed
                    ? product.name
                    : `${product.productKey} — ${product.name}`,
            }));
        },
        getBrands: state => state.brands,
        getSuppliers: state => state.suppliers,
    },

    /**
     * Default mutations available:
     * - setLoading
     * - setProducts
     * - deleteProduct
     * - updateProduct
     * - addProduct
     * - setSelectedFilter
     * - resetFilter
     * - resetState
     */
    mutations: {
        setProductBrands(state, brands) {
            state.brands = brands;
        },
        setProductSuppliers(state, suppliers) {
            state.suppliers = suppliers;
        },
        addProductsWithMinimalData(state, productsWithMinimalData) {
            state.productsWithMinimalData = productsWithMinimalData;
        },
        setProductsForPromotionProductOfferGroups(state, { productOfferGroupId, products }) {
            if (!productOfferGroupId) {
                state.productsForPromotionProductOfferGroups = {};
            } else {
                state.productsForPromotionProductOfferGroups[productOfferGroupId] = products;
            }
        },
    },

    /**
     * Default actions available:
     * - fetchProducts
     * - deleteProduct
     * - updateProduct
     * - updateProducts
     * - createProduct
     * - handleResponseNotifications
     * - setSelectedFilter
     * - resetFilter
     * - resetState
     */
    actions: {
        fetchProductsBySupplierAndCategory({ rootState, dispatch }, { supplier, categories }) {
            if (isNil(supplier) || isEmpty(categories)) {
                return;
            }
            return dispatch('fetchProducts', {
                params: {
                    where: {
                        supplierKey: supplier,
                        hierarchy: {
                            $elemMatch: {
                                level: rootState.clientConfig.hierarchyConfig.categoryLevel,
                                levelEntryKey: { $in: categories },
                            },
                        },
                    },
                },
                patchState: true,
                patchOptions: {
                    fieldToMatchOn: '_id',
                    isMatchFunction: (
                        product,
                        {
                            supplierKey,
                            hierarchy: {
                                $elemMatch: {
                                    level: categoryLevel,
                                    levelEntryKey: { $in: categoryKeys },
                                },
                            },
                        }
                    ) =>
                        isProductMatchSupplierAndCategory({
                            product,
                            supplierKey,
                            categoryKeys,
                            categoryLevel,
                        }),
                },
            });
        },
        async fetchProductBrands({ commit, dispatch }, { params = {} } = {}) {
            const [error, response] = await to(axios.get(`/api/products/brands`, { params }));

            if (error) {
                dispatch('handleResponseNotifications', {
                    error,
                    errorMessage: i18n.t('notifications.fetchError', { resource: 'brands' }),
                });

                return;
            }

            commit('setProductBrands', response.data);
        },

        async fetchProductBrandsByCategory({ dispatch, rootState }) {
            const selectedCategory = rootState.promotionCandidates.selectedCategory;
            if (!isNil(selectedCategory)) {
                return dispatch('fetchProductBrands', {
                    params: {
                        where: {
                            hierarchy: {
                                $elemMatch: {
                                    level: rootState.clientConfig.hierarchyConfig.categoryLevel,
                                    levelEntryKey: selectedCategory,
                                },
                            },
                        },
                    },
                });
            }
        },

        async fetchProductSuppliers({ commit, dispatch }) {
            const [error, response] = await to(axios.get(`/api/products/suppliers`));

            if (error) {
                dispatch('handleResponseNotifications', {
                    error,
                    errorMessage: i18n.t('notifications.fetchError', {
                        resource: i18n.t('entities.suppliers', 2),
                    }),
                });

                return;
            }
            commit('setProductSuppliers', response.data);
        },
        async fetchProductsWithMinimalData(
            { commit, dispatch },
            { query, returnDocuments = false, storeDocuments = false } = {}
        ) {
            const pick = [
                '{ "_id": 1 }',
                '{ "productKey": 1 }',
                '{ "MotherProductKey": 1 }',
                '{ "name": 1 }',
                '{ "category": 1 }',
                '{ "clientProductKey": 1 }',
                '{ "brandDescription": 1 }',
                '{ "supplierName": 1 }',
                '{ "standardWeight": 1 }',
                '{ "packSize": 1 }',
                '{ "isUnitProduct": 1 }',
            ];
            let params = {};
            if (query) {
                params = {
                    where: query,
                    pick,
                };
            } else {
                params = {
                    pick,
                };
            }
            const [error, response] = await to(
                axios.get('/api/products/promotable-products', { params })
            );
            if (error) {
                dispatch('handleResponseNotifications', {
                    error,
                    errorMessage: i18n.t('notifications.fetchError', {
                        resource: 'promotion-candidates',
                    }),
                });
            }

            if (storeDocuments) {
                commit('addProductsWithMinimalData', response.data);
            }
            if (returnDocuments) {
                return response.data;
            }
        },

        async fetchProductsAndAttributesForPromotionProductOfferGroup(
            { commit, dispatch },
            { productOfferGroup, attributes, returnDocuments = false, storeDocuments = false }
        ) {
            commit('setLoading', true);
            let productsForPromotion = await dispatch(
                'fetchProductsForPromotionProductOfferGroup',
                { productOfferGroup, returnDocuments: true, storeDocuments }
            );

            const productOfferGroupId = productOfferGroup._id;

            if (size(productsForPromotion) && size(attributes)) {
                productsForPromotion = await dispatch('fetchProductAttributesForPromotion', {
                    productOfferGroupId,
                    attributes,
                    productsForPromotion,
                    returnDocuments: true,
                });
            }

            if (storeDocuments) {
                commit('setProductsForPromotionProductOfferGroups', {
                    productOfferGroupId,
                    products: productsForPromotion,
                });
            }
            commit('setLoading', false);
            if (returnDocuments) {
                return productsForPromotion;
            }
        },

        resetProductsForPromotionProductOfferGroup({ commit }, { productOfferGroup }) {
            commit('setProductsForPromotionProductOfferGroups', {
                productOfferGroupId: productOfferGroup._id,
                products: [],
            });
        },

        async fetchProductAttributesForPromotion(
            { state, commit, dispatch },
            {
                productOfferGroupId,
                attributes,
                productsForPromotion,
                returnDocuments = false,
                storeDocuments = false,
            } = {}
        ) {
            if (!productsForPromotion) {
                productsForPromotion = cloneDeep(
                    state.productsForPromotionProductOfferGroups[productOfferGroupId]
                );
            }

            // Determine the list of categories that we require.
            const productCategories = uniq(map(productsForPromotion, 'category'));

            // Identify which attributes we already have data for by comparing the list
            // of attributes on each product and taking the intersection across all products.
            const attributesAcrossProducts = map(
                productsForPromotion,
                product => new Set(map(product.attributes || [], 'attributeKey'))
            );

            const requiredAttributes = attributes.filter(
                attribute =>
                    // Require attribute if we do not already have it for each product
                    !attributesAcrossProducts.every(productSet => productSet.has(attribute))
            );

            if (productCategories.length && requiredAttributes.length) {
                const params = {
                    where: {
                        requiredCategories: productCategories,
                        attributes: requiredAttributes,
                    },
                };
                commit('setLoading', true);
                const [error, response] = await to(
                    axios.get('/api/products/product-attributes', {
                        params,
                    })
                );
                if (error) {
                    dispatch('handleResponseNotifications', {
                        error,
                        errorMessage: i18n.t('notifications.fetchError', {
                            resource: 'promotion-candidates',
                        }),
                    });
                }

                const productAttributes = response.data;
                const keyedProductAttributes = keyBy(productAttributes, 'productKey');

                productsForPromotion.forEach(product => {
                    if (keyedProductAttributes[product.productKey]) {
                        product.attributes = unionBy(
                            product.attributes,
                            keyedProductAttributes[product.productKey].attributes,
                            'attributeKey'
                        );
                    }
                });

                if (storeDocuments) {
                    commit('setProductsForPromotionProductOfferGroups', {
                        productOfferGroupId,
                        products: productsForPromotion,
                    });
                }
                commit('setLoading', false);
            }

            if (returnDocuments) {
                return productsForPromotion;
            }
        },

        async fetchProductsForPromotionProductOfferGroup(
            { commit, dispatch },
            { productOfferGroup, returnDocuments = false, storeDocuments = false } = {}
        ) {
            // Get the categories this promotion is for
            const productOfferGroupCategories = get(
                productOfferGroup,
                'userSelectedCategories',
                []
            );

            // Exit early if a categories have not been selected yet.
            if (!productOfferGroupCategories.length) return [];

            const userSelectedCategories = productOfferGroupCategories.map(
                ({ levelEntryKey }) => levelEntryKey
            );

            const productsAvailableForPromotion = await dispatch('fetchProductsWithMinimalData', {
                query: { userSelectedCategoryIds: userSelectedCategories },
                returnDocuments: true,
                storeDocuments,
            });

            if (storeDocuments) {
                commit('setProductsForPromotionProductOfferGroups', {
                    productOfferGroupId: productOfferGroup._id,
                    products: productsAvailableForPromotion,
                });
            }

            if (returnDocuments) {
                return productsAvailableForPromotion;
            }
        },
        async fetchProductsNumberInPromotion(
            { commit, dispatch, rootState },
            { categories, attributeScope, attributeSegments, uuid }
        ) {
            commit('setLoading', true);
            const payload = {
                categories,
                attributeScope,
            };

            if (isMultipleAttributeSegmentsEnabled(rootState)) {
                payload.attributeSegments = attributeSegments;
            }

            const [error, response] = await to(
                axios.post('/api/products/get-products-count', payload)
            );
            commit('setLoading', false);
            if (error) {
                dispatch('handleResponseNotifications', {
                    error,
                    errorMessage: i18n.t('notifications.fetchError', {
                        resource: 'products',
                    }),
                });
                return;
            }
            return { count: response.data.count, uuid };
        },
        async fetchBigAmountOfProducts({ commit, dispatch }, { keys }) {
            commit('setLoading', true);
            const [error, response] = await to(
                axios.post('/api/products/get-big-amount-of-products', {
                    keys,
                })
            );
            commit('setLoading', false);
            if (error) {
                dispatch('handleResponseNotifications', {
                    error,
                    errorMessage: i18n.t('notifications.fetchError', {
                        resource: 'products',
                    }),
                });
                return;
            }
            return response.data;
        },
        async fetchPromotionAttributeFilterOptions(
            { dispatch, rootState },
            {
                categories = [],
                orderedSelections = [],
                segments = [],
                scopeToken = null,
                requestId = null,
            } = {}
        ) {
            const normalizedCategories = normalizeFilterCategories(categories);
            const normalizedOrderedSelections = normalizeOrderedSelections(orderedSelections);
            const normalizedSegments = isMultipleAttributeSegmentsEnabled(rootState)
                ? normalizeAttributeSegments(segments)
                : [];
            const requestState = getPromotionAttributeFilterRequestState(scopeToken);

            requestState.requestSequence += 1;
            const activeRequestSequence = requestState.requestSequence;
            if (requestState.abortController) {
                requestState.abortController.abort();
            }
            requestState.abortController = new AbortController();

            const [error, response] = await to(
                axios.post(
                    '/api/products/attribute-filter-options',
                    getAttributeFilterPayload({
                        categories: normalizedCategories,
                        orderedSelections: normalizedOrderedSelections,
                        segments: normalizedSegments,
                        scopeToken,
                    }),
                    {
                        signal: requestState.abortController.signal,
                    }
                )
            );

            if (activeRequestSequence !== requestState.requestSequence) {
                return {
                    requestId,
                    ignored: true,
                };
            }

            if (error) {
                clearPromotionAttributeFilterAbortControllerIfCurrent({
                    requestState,
                    requestSequence: activeRequestSequence,
                });

                if (isRequestCancelled(error)) {
                    return {
                        requestId,
                        cancelled: true,
                    };
                }

                dispatch('handleResponseNotifications', {
                    error,
                    errorMessage: i18n.t('notifications.fetchError', {
                        resource: 'products',
                    }),
                });

                return {
                    requestId,
                    error: true,
                };
            }

            clearPromotionAttributeFilterAbortControllerIfCurrent({
                requestState,
                requestSequence: activeRequestSequence,
            });

            return {
                requestId,
                data: response.data,
            };
        },
        applyPromotionAttributeFilterResponse(
            _,
            { attributeScope = [], attributeSegments = [], response = {} } = {}
        ) {
            const availableAttributeKeysByRowKey = {};
            const availableValuesByRowKey = {};
            const segments = get(response, 'segments', []);

            if (segments.length) {
                const availableAttributeKeysBySegmentId = {};
                const availableValuesBySegmentId = {};
                const responseSegmentsById = keyBy(segments, 'segmentId');

                attributeSegments.forEach((segment, segmentIndex) => {
                    const segmentId =
                        segment.segmentId || segment.id || segment.key || String(segmentIndex);
                    const segmentResponse =
                        responseSegmentsById[segmentId] || segments[segmentIndex] || {};
                    const appliedSegmentResponse = applyAttributeFilterResponseToRows({
                        attributeScope: segment.rows || [],
                        response: segmentResponse,
                    });

                    availableAttributeKeysBySegmentId[segmentId] =
                        appliedSegmentResponse.availableAttributeKeysByRowKey;
                    availableValuesBySegmentId[segmentId] =
                        appliedSegmentResponse.availableValuesByRowKey;
                    merge(
                        availableAttributeKeysByRowKey,
                        appliedSegmentResponse.availableAttributeKeysByRowKey
                    );
                    merge(availableValuesByRowKey, appliedSegmentResponse.availableValuesByRowKey);
                });

                return {
                    scopedProductCount: get(response, 'scopedProductCount', 0),
                    invalidatedSelections: get(response, 'invalidatedSelections', []),
                    availableAttributeKeysByRowKey,
                    availableValuesByRowKey,
                    availableAttributeKeysBySegmentId,
                    availableValuesBySegmentId,
                    segments,
                };
            }

            return applyAttributeFilterResponseToRows({ attributeScope, response });
        },
    },
};

const mixinParams = {
    resource: 'product',
    useForm: true,
    getInitialState,
};

export default merge({}, storeMixin(mixinParams), store);
